import React, { useState, useEffect, useRef } from 'react';

// openlayers
import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import XYZ from 'ol/source/XYZ'
import { Coordinate } from 'ol/coordinate';
import { Geometry } from 'ol/geom';
import { Feature, MapBrowserEvent } from 'ol';
import { Style, Icon, Text, Fill, Stroke } from 'ol/style';
import 'ol/ol.css';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileLayerSource from 'ol/source/VectorTile';
import MVT from 'ol/format/MVT';
import { transform } from 'ol/proj';
import GeoJSON from 'ol/format/GeoJSON';
import mapJSON from './assets/map.json';
import mapStyle from './assets/style.json';
import stylefunction from 'ol-mapbox-style/dist/stylefunction';

interface MapProps {
    features: Feature<Geometry>[],
    center: Coordinate,
    zoom: number,
    height?: number | string,
}

interface MarkerInfo {
    name: string,
    address: string,
    url: string,
}

const MapWrapper = ({ features, center, zoom, height = 200 }: MapProps) => {

    // set intial state
    const [map, setMap] = useState<Map>()
    const [featuresLayer, setFeaturesLayer] = useState<VectorLayer<VectorSource<Geometry>>>()
    const [selectedCoord, setSelectedCoord] = useState<Coordinate>()
    const [currentMarker, setCurrentMarker] = useState<MarkerInfo>();

    // pull refs
    const mapElement = useRef<HTMLDivElement>(null)

    // create state ref that can be accessed in OpenLayers onclick callback function
    //  https://stackoverflow.com/a/60643670
    const mapRef = useRef<Map>()
    mapRef.current = map

    // initialize map on first render - logic formerly put into componentDidMount
    useEffect(() => {
        if (!map) {
            // create map
            let textStyle = new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.6)'
                }),
                stroke: new Stroke({
                    color: '#319FD3',
                    width: 1
                }),
                text: new Text({
                    overflow: true,
                    font: '18px Calibri,sans-serif',
                    fill: new Fill({
                        color: '#000'
                    }),
                    stroke: new Stroke({
                        color: '#fff',
                        width: 3
                    })
                })
            });

            let strokeStyle = new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.6)'
                }),
                stroke: new Stroke({
                    color: '#319FD3',
                    width: 1
                }),
            })

            const mapLayer = new VectorTileLayer({
                source: new VectorTileLayerSource({
                    format: new MVT(),
                    url: 'https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer/tile/{z}/{y}/{x}.pbf'
                })
            })

            stylefunction(mapLayer, mapStyle, 'esri')

            const initialMap = new Map({
                target: mapElement.current || undefined,
                layers: [
                    mapLayer,
                    // new VectorTileLayer({
                    //     source: new VectorTileLayerSource({
                    //         format: new MVT(),
                    //         url: 'https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer/tile/{z}/{y}/{x}.pbf'
                    //     })
                    // }),
                    // USGS Topo
                    // new TileLayer({
                    //     source: new XYZ({
                    //         attributions: 'Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012',
                    //         url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
                    //         crossOrigin: 'anonymous'
                    //     }),
                    // }),
                    new VectorLayer({
                        source: new VectorSource({
                            features: (new GeoJSON().readFeatures(mapJSON, { featureProjection: 'EPSG:3857' })),
                        }),
                        opacity: 0.7,
                        style: function (feature) {
                            if ((feature.get('Name_EN') === "Hoang Sa (Da Nang)" || feature.get('Name_EN') === "Truong Sa (Khanh Hoa)")
                                && !feature.get('key')) {
                                return strokeStyle
                            }

                            if (feature.get('key') === "Hoang Sa") {
                                let newStyle = textStyle;
                                newStyle.getText().setText('Hoàng Sa (Đà Nẵng)');
                                return newStyle;
                            }

                            if (feature.get('key') === "Truong Sa") {
                                let newStyle = textStyle;
                                newStyle.getText().setText('Trường Sa (Khánh Hoà)');
                                return newStyle;
                            }
                            return undefined;
                        }
                    }),

                    // Google Maps Terrain
                    /* new TileLayer({
                      source: new XYZ({
                        url: 'http://mt0.google.com/vt/lyrs=p&hl=en&x={x}&y={y}&z={z}',
                      })
                    }), */

                    // initalFeaturesLayer

                ],
                view: new View({
                    projection: 'EPSG:3857',
                    center,
                    zoom
                }),
            })

            // olms('map-container', 'https://www.arcgis.com/sharing/rest/content/items/de26a3cf4cc9451298ea173c4b324736/resources/styles/root.json?f=pjson')

            // set map onclick handler
            initialMap.on('click', handleMapClick)

            // save map and vector layer references to state
            setMap(initialMap)
        }
    }, [center, zoom, map])

    // update map if features prop changes - logic formerly put into componentDidUpdate
    useEffect(() => {

        if (features && features.length && map) { // may be null on first render

            const initalFeaturesLayer = new VectorLayer({
                source: new VectorSource({
                    features
                }),
                style: new Style({
                    image: new Icon({
                        anchor: [0.5, 46],
                        anchorXUnits: 'fraction',
                        anchorYUnits: 'pixels',
                        src: 'https://openlayers.org/en/latest/examples/data/icon.png'
                    })
                })
            })

            map.addLayer(initalFeaturesLayer)
            // set features to map
            // featuresLayer.setSource(
            //     new VectorSource({
            //         features// make sure features is an array,
            //     })
            // )

        }

    }, [features, featuresLayer, map])

    // map click handler
    const handleMapClick = (event: MapBrowserEvent<any>) => {

        // get clicked coordinate using mapRef to access current React state inside OpenLayers callback
        //  https://stackoverflow.com/a/60643670
        // const clickedCoord = mapRef.current!.getCoordinateFromPixel(event.pixel);
        // console.log(clickedCoord)

        // transform coord to EPSG 4326 standard Lat Long
        // const transformedCoord = transform(clickedCoord, 'EPSG:3857', 'EPSG:4326')

        // set React state
        // setSelectedCoord(clickedCoord)

        let feature = mapRef.current!.forEachFeatureAtPixel(event.pixel, (item) => {
            return item;
        })

        if (feature && mapRef.current) {
            let properties = feature.getProperties();
            if (properties.name && properties.address && properties.url && properties.location) {
                let clickedCoord = transform(properties.location, 'EPSG:4326', 'EPSG:3857');

                mapRef.current.getView().animate({ zoom, center: clickedCoord });
                setCurrentMarker({
                    name: properties.name,
                    address: properties.address,
                    url: properties.url,
                })
            }
        }
    }

    // render component
    return (
        <div style={{ width: '100%', height, position: 'relative', padding: 10 }}>

            {currentMarker ?
                <div className="map-marker-info">
                    <div className='font-bold'>{currentMarker.name}</div>
                    <div>{currentMarker.address}</div>
                    <a href={currentMarker.url} target="_blank" rel="noreferrer">Xem bản đồ lớn hơn</a>
                </div>
                : <></>}
            <div ref={mapElement} className="map-container" style={{ width: '100%', height: '100%' }}></div>
        </div>
    )

}

export default MapWrapper